import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { signOut as firebaseSignOut, updatePassword } from 'firebase/auth';
import { IUsuario } from '../interfaces/IUsuario';
import AuthService from '../services/AuthService';
import Storage from '../utils/StorageUtils';
import { INotificacaoSistema } from '../interfaces/INotificacaoSistema';
import MensagensService from '../services/MensagensService';
import { firebaseAuth } from '../services/FirebaseConfig';
import api, { encerrarSessao } from '../services/Api';
import { IAuthenticationResult } from '../interfaces/IAuthenticationResult';
import { sessaoExpirada } from '../utils/sessao';
import { iniciarRenovacaoToken } from '../services/SessaoService';

interface IAuthContext {
    isAuthorized: Boolean,
    loading: Boolean,
    mustChangePassword: boolean,
    usuario: IUsuario | undefined,
    token: string | null,
    signIn: any,
    signOut: any,
    recoverPassword: any,
    completePasswordChange: (newPassword: string) => Promise<void>,
}

const AuthContext = createContext<IAuthContext | null>(null);

export const AuthProvider = ({ children }: any) => {
    const [isAuthorized, setAuthorized] = useState<boolean>(false);
    const [token, setToken] = useState<string | null>(null);
    const [usuario, setUsuario] = useState<IUsuario | undefined>({} as IUsuario);
    const [loading, setLoading] = useState<boolean>(true);
    const [mustChangePassword, setMustChangePassword] = useState<boolean>(false);

    useEffect(() => {
        async function loadStorageData() {
            enableSplashScreen();
            const storagedUsuario = await Storage.getUsuario();
            const storageToken = await Storage.getToken();

            if (storagedUsuario && storageToken && sessaoExpirada(Storage.getSessaoInicio())) {
                await encerrarSessao();
            } else if (storagedUsuario && storageToken) {
                setUsuario(storagedUsuario);
                setToken(storageToken);
                setAuthorized(true);
            }

            setLoading(false);
            disableSplashScreen();
        }

        loadStorageData();
    }, []);

    useEffect(() => {
        if (!isAuthorized) return;
        return iniciarRenovacaoToken();
    }, [isAuthorized]);


    const enableSplashScreen = () => {
        const splashScreen = document.getElementById('splash-screen')
        if (splashScreen) {
            splashScreen.style.setProperty('display', 'flex')
        }
    }

    const disableSplashScreen = () => {
        const splashScreen = document.getElementById('splash-screen')
        if (splashScreen) {
            splashScreen.style.setProperty('display', 'none')
        }
    }

    async function signIn(email: string, password: string): Promise<{ mustChangePassword: boolean } | void> {
        try {
            const result = await AuthService.signIn(email, password);

            if (result === null) {
                throw new Error("Tratar erro");
            }

            if (result.mustChangePassword) {
                setMustChangePassword(true);
                return { mustChangePassword: true };
            }

            await Storage.setUsuario(result.usuario);
            await Storage.setToken(result.jwt);
            Storage.setSessaoInicio(Date.now());

            setUsuario(result.usuario);
            setToken(result.jwt);
            setAuthorized(true);
            setMustChangePassword(false);

            return { mustChangePassword: false };
        } catch (error) {
            console.log(error);
            throw error;
        }
    }

    async function completePasswordChange(newPassword: string): Promise<void> {
        const firebaseUser = firebaseAuth.currentUser;
        if (!firebaseUser) throw new Error('Sessão expirada. Faça login novamente.');

        await updatePassword(firebaseUser, newPassword);

        const idToken = await firebaseUser.getIdToken(true);

        await api.post('auth/change-password', {}, {
            baseURL: process.env.REACT_APP_API_CORE_URL ?? 'https://api-core.dev.painel-agholmes.com/api/v3',
            headers: { Authorization: `Bearer ${idToken}` },
        });

        const { data: result } = await api.post<IAuthenticationResult>('auth', { idToken }, {
            baseURL: process.env.REACT_APP_API_CORE_URL ?? 'https://api-core.dev.painel-agholmes.com/api/v3',
        });

        await Storage.setUsuario(result.usuario);
        await Storage.setToken(result.jwt);
        Storage.setSessaoInicio(Date.now());

        setUsuario(result.usuario);
        setToken(result.jwt);
        setAuthorized(true);
        setMustChangePassword(false);
    }

    async function recoverPassword(newPassword: string, keyUrl: string): Promise<string | void> {
        const result = await AuthService.recoverPassword(newPassword, keyUrl);
        return result;
    }

    async function signOut(): Promise<void> {
        try {
            await Storage.clearStorage();
            await firebaseSignOut(firebaseAuth).catch(() => undefined);
            setAuthorized(false);
            setUsuario(undefined);
            setToken(null);
            setMustChangePassword(false);
        } catch (error) {
            alert("Login não pode ser realizado");
        }
    }

    return (
        <AuthContext.Provider value={{ isAuthorized, recoverPassword, mustChangePassword, usuario, token, loading, signIn, signOut, completePasswordChange }}>
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    const context = useContext<IAuthContext | any>(AuthContext);

    return context as IAuthContext;
}
